import { ReloadOutlined } from '@ant-design/icons';
import type { MaterialRow, PurchaseRow, RequireRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Drawer,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hq } from '../session';
import PurchaseDetailBody from '../components/purchase/PurchaseDetailBody';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

const ORDER_STATUS: Record<string, { text: string; color: string }> = {
  DRAFT: { text: '草稿', color: 'default' },
  SUBMITTED: { text: '已提交', color: 'processing' },
  APPROVED: { text: '已审批', color: 'cyan' },
  SENT: { text: '已下达', color: 'blue' },
  ACKED: { text: '已接单', color: 'blue' },
  PART_RECEIVED: { text: '部分到货', color: 'orange' },
  RECEIVED: { text: '已到货', color: 'green' },
  CHECKED: { text: '已验收', color: 'green' },
  CLOSED: { text: '已关闭', color: 'default' },
  CANCELED: { text: '已取消', color: 'default' },
  TERMINATED: { text: '已终止', color: 'red' }
};

const REQUIRE_STATUS: Record<string, { text: string; color: string }> = {
  DRAFT: { text: '草稿', color: 'default' },
  SUBMITTED: { text: '已提交', color: 'processing' },
  APPROVED: { text: '已审批', color: 'cyan' },
  ORDERED: { text: '已转采购', color: 'green' },
  CLOSED: { text: '已关闭', color: 'default' },
  CANCELED: { text: '已取消', color: 'default' }
};

/** 后端 order_status 支持任意值过滤，这里覆盖全生命周期（含终态与草稿）。 */
type OrderFilter =
  | 'ALL'
  | 'DRAFT'
  | 'SUBMITTED'
  | 'APPROVED'
  | 'SENT'
  | 'ACKED'
  | 'PART_RECEIVED'
  | 'RECEIVED'
  | 'CHECKED'
  | 'CLOSED'
  | 'CANCELED'
  | 'TERMINATED';

const ORDER_FILTERS: { label: string; value: OrderFilter }[] = [
  { label: '全部', value: 'ALL' },
  { label: '草稿', value: 'DRAFT' },
  { label: '已提交', value: 'SUBMITTED' },
  { label: '已审批', value: 'APPROVED' },
  { label: '已下达', value: 'SENT' },
  { label: '已接单', value: 'ACKED' },
  { label: '部分到货', value: 'PART_RECEIVED' },
  { label: '已到货', value: 'RECEIVED' },
  { label: '已验收', value: 'CHECKED' },
  { label: '已关闭', value: 'CLOSED' },
  { label: '已取消', value: 'CANCELED' },
  { label: '已终止', value: 'TERMINATED' }
];

/** 请购单状态筛选项（后端 require_status）。 */
type RequireFilter = 'ALL' | 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'ORDERED' | 'CLOSED' | 'CANCELED';

interface ReceiveDraft {
  itemId: string;
  materialName: string | null;
  qty: number;
  remain: number;
  batchNo: string;
}

/** 建单行草稿（请购 / 采购共用）。 */
interface DraftLine {
  key: string;
  materialId: string;
  materialName?: string;
  spec?: string;
  unitId?: string;
  unitName?: string;
  qty: number;
  unitPrice: number;
}

let draftSeq = 0;
const newDraftLine = (): DraftLine => ({ key: `line-${++draftSeq}`, materialId: '', qty: 1, unitPrice: 0 });

/** 采购中心：请购单 / 采购订单 / 到货收货（生成入库单，后续在入库确认写库存）。 */
export default function PurchaseOps() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [orderFilter, setOrderFilter] = useState<OrderFilter>('ALL');
  const [requireFilter, setRequireFilter] = useState<RequireFilter>('ALL');
  const [requirePeriod, setRequirePeriod] = useState('');
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [receiveDrafts, setReceiveDrafts] = useState<ReceiveDraft[]>([]);
  const [warehouseId, setWarehouseId] = useState('');

  // ---------- 建单（请购 / 采购） ----------
  const [requireOpen, setRequireOpen] = useState(false);
  const [requirePeriodInput, setRequirePeriodInput] = useState(dayjs().format('YYYY-MM'));
  const [requireRemark, setRequireRemark] = useState('');
  const [requireLines, setRequireLines] = useState<DraftLine[]>([]);

  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [buySupplierId, setBuySupplierId] = useState('');
  const [buyWarehouseId, setBuyWarehouseId] = useState('');
  const [buyExpectArrive, setBuyExpectArrive] = useState('');
  const [buyRemark, setBuyRemark] = useState('');
  const [purchaseLines, setPurchaseLines] = useState<DraftLine[]>([]);

  const suppliers = useQuery({
    queryKey: ['ops', 'catalog', 'suppliers'],
    queryFn: () => hq.catalog.suppliers({ size: 200 }),
    staleTime: 10 * 60 * 1000
  });

  const materials = useQuery({
    queryKey: ['ops', 'material', 'options'],
    queryFn: () => hq.material.list({ size: 500 }),
    staleTime: 10 * 60 * 1000
  });

  const warehouses = useQuery({
    queryKey: ['ops', 'org-structure', 'warehouse'],
    queryFn: () => hq.orgStructure.warehouse.list(),
    staleTime: 10 * 60 * 1000
  });

  const supplierOptions = (suppliers.data?.items ?? []).map((row) => ({
    value: row.id,
    label: row.supplierName ?? row.id
  }));

  const materialOptions = (materials.data?.items ?? []).map((row) => ({
    value: row.id,
    label: `${row.materialName ?? row.id}${row.spec ? ` / ${row.spec}` : ''}`,
    material: row
  }));

  const warehouseOptions = (warehouses.data ?? []).map((row) => ({
    value: row.id,
    label: `${row.whName}（${row.whCode}）`
  }));

  const createRequire = useMutation({
    mutationFn: () =>
      hq.procurement.requires.create({
        requirePeriod: requirePeriodInput || undefined,
        remark: requireRemark.trim() || undefined,
        items: requireLines
          .filter((line) => line.materialId && line.qty > 0)
          .map((line) => ({
            materialId: line.materialId,
            materialName: line.materialName,
            spec: line.spec,
            unitId: line.unitId,
            unitName: line.unitName,
            reqQty: line.qty,
            unitPrice: line.unitPrice
          }))
      }),
    onSuccess: async (result) => {
      message.success(`请购单已生成：${result.require.bizNo}`);
      setRequireOpen(false);
      setRequireLines([]);
      await queryClient.invalidateQueries({ queryKey: ['ops', 'require'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '建请购单失败')
  });

  const createPurchase = useMutation({
    mutationFn: () =>
      hq.procurement.purchases.create({
        supplierId: buySupplierId,
        supplierName: supplierOptions.find((option) => option.value === buySupplierId)?.label,
        warehouseId: buyWarehouseId,
        expectArriveAt: buyExpectArrive ? dayjs(buyExpectArrive).toISOString() : undefined,
        remark: buyRemark.trim() || undefined,
        items: purchaseLines
          .filter((line) => line.materialId && line.qty > 0)
          .map((line) => ({
            materialId: line.materialId,
            materialName: line.materialName,
            spec: line.spec,
            unitId: line.unitId,
            unitName: line.unitName,
            qty: line.qty,
            unitPrice: line.unitPrice
          }))
      }),
    onSuccess: async (result) => {
      message.success(`采购单已生成：${result.order.bizNo}`);
      setPurchaseOpen(false);
      setPurchaseLines([]);
      await queryClient.invalidateQueries({ queryKey: ['ops', 'purchase'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '建采购单失败')
  });

  const requires = useInfiniteQuery({
    queryKey: ['ops', 'require', 'list', requireFilter, requirePeriod],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.procurement.requires.list({
        lastId: pageParam,
        size: PAGE_SIZE,
        requireStatus: requireFilter === 'ALL' ? undefined : requireFilter,
        requirePeriod: requirePeriod || undefined
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const orders = useInfiniteQuery({
    queryKey: ['ops', 'purchase', 'list', orderFilter],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.procurement.purchases.list({
        lastId: pageParam,
        size: PAGE_SIZE,
        orderStatus: orderFilter === 'ALL' ? undefined : orderFilter
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const detail = useQuery({
    queryKey: ['ops', 'purchase', 'detail', currentId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.procurement.purchases.detail(currentId ?? ''),
    enabled: Boolean(currentId)
  });

  const receive = useMutation({
    mutationFn: (payload: { id: string; warehouseId: string; items: ReceiveDraft[] }) =>
      hq.procurement.purchases.receive(payload.id, {
        warehouseId: payload.warehouseId,
        items: payload.items
          .filter((item) => item.qty > 0)
          .map((item) => ({ itemId: item.itemId, qty: item.qty, batchNo: item.batchNo || undefined }))
      }),
    onSuccess: async (result) => {
      message.success(`已生成入库单 ${result.inboundNo}，待入库确认`);
      setReceiveOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['ops', 'purchase'] });
      await detail.refetch();
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '收货失败，请稍后重试');
    }
  });

  const ack = useMutation({
    mutationFn: (id: string) => hq.procurement.purchases.ack(id),
    onSuccess: async () => {
      message.success('已接单');
      await queryClient.invalidateQueries({ queryKey: ['ops', 'purchase'] });
      await detail.refetch();
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '接单失败');
    }
  });

  const openReceive = () => {
    const data = detail.data;
    if (!data) return;

    setWarehouseId(data.order.deliveryWarehouseId === '0' ? '' : data.order.deliveryWarehouseId);
    setReceiveDrafts(
      data.items
        .filter((item) => item.receivedQty < item.qty)
        .map((item) => ({
          itemId: item.id,
          materialName: item.materialName,
          qty: item.qty - item.receivedQty,
          remain: item.qty - item.receivedQty,
          batchNo: item.batchNo ?? ''
        }))
    );
    setReceiveOpen(true);
  };

  const requireRows = requires.data?.pages.flatMap((page) => page.items) ?? [];
  const orderRows = orders.data?.pages.flatMap((page) => page.items) ?? [];

  const requireColumns: ColumnsType<RequireRow> = [
    { title: '请购单号', dataIndex: 'bizNo', width: 220 },
    { title: '期间', dataIndex: 'requirePeriod', width: 110, render: (value: string | null) => value || '-' },
    { title: '类型', dataIndex: 'flowType', width: 100, render: (value: string | null) => value || '-' },
    {
      title: '状态',
      dataIndex: 'requireStatus',
      width: 110,
      render: (value: string) => {
        const meta = REQUIRE_STATUS[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '总数量', dataIndex: 'totalQty', width: 110, align: 'right', render: (value: number) => formatQty(value) },
    { title: '总金额', dataIndex: 'totalAmount', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
    { title: '转采购单', dataIndex: 'convertedOrderNos', ellipsis: { showTitle: false }, render: ellipsisCell('convertedOrderNos') },
    { title: '业务时间', dataIndex: 'bizDate', width: 160, render: (value: string | null) => formatDisplayTime(value) }
  ];

  const orderColumns: ColumnsType<PurchaseRow> = [
    {
      title: '采购单号',
      dataIndex: 'bizNo',
      width: 220,
      render: (value: string, row) => <Typography.Link onClick={() => setCurrentId(row.id)}>{value}</Typography.Link>
    },
    { title: '供应商', dataIndex: 'supplierName', ellipsis: { showTitle: false }, render: ellipsisCell('supplierName') },
    {
      title: '状态',
      dataIndex: 'orderStatus',
      width: 110,
      render: (value: string) => {
        const meta = ORDER_STATUS[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '总数量', dataIndex: 'totalQty', width: 110, align: 'right', render: (value: number) => formatQty(value) },
    { title: '已收数量', dataIndex: 'receivedQty', width: 110, align: 'right', render: (value: number) => formatQty(value) },
    { title: '总金额', dataIndex: 'totalAmount', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
    { title: '预计到货', dataIndex: 'expectArriveAt', width: 160, render: (value: string | null) => formatDisplayTime(value) },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_value: unknown, row) => (
        <Button type="link" onClick={() => setCurrentId(row.id)}>
          详情
        </Button>
      )
    }
  ];

  return (
    <Card
      className="page-card"
      title="采购中心"
      extra={
        <Button
          icon={<ReloadOutlined />}
          loading={requires.isFetching || orders.isFetching}
          onClick={() => {
            void requires.refetch();
            void orders.refetch();
          }}
        >
          刷新
        </Button>
      }
    >
      <Tabs
        items={[
          {
            key: 'purchase',
            label: '采购订单',
            children: (
              <>
                <Space style={{ marginBottom: 12 }} wrap>
                  <Select<OrderFilter>
                    value={orderFilter}
                    options={ORDER_FILTERS}
                    onChange={(value) => setOrderFilter(value)}
                    style={{ width: 150 }}
                  />
                  <Button
                    type="primary"
                    onClick={() => {
                      setBuySupplierId('');
                      setBuyWarehouseId(warehouseId);
                      setBuyExpectArrive('');
                      setBuyRemark('');
                      setPurchaseLines([newDraftLine()]);
                      setPurchaseOpen(true);
                    }}
                  >
                    新建采购单
                  </Button>
                </Space>
                {orders.isError ? (
                  <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="采购单加载失败"
                    description={orders.error instanceof ApiError ? orders.error.friendlyMessage : String(orders.error)}
                  />
                ) : null}
                <Table<PurchaseRow>
                  rowKey="id"
                  size="middle"
                  columns={orderColumns}
                  dataSource={orderRows}
                  loading={orders.isPending}
                  pagination={false}
                  scroll={{ x: sumWidth(orderColumns) }}
                  locale={{ emptyText: '暂无采购订单' }}
                  onRow={(row) => ({ onDoubleClick: () => setCurrentId(row.id) })}
                />
                <Space style={{ marginTop: 16 }}>
                  <Typography.Text type="secondary">已加载 {orderRows.length} 条</Typography.Text>
                  {orders.hasNextPage ? (
                    <Button onClick={() => orders.fetchNextPage()} loading={orders.isFetchingNextPage}>
                      加载更多
                    </Button>
                  ) : null}
                </Space>
              </>
            )
          },
          {
            key: 'require',
            label: '请购单',
            children: (
              <>
                <Space style={{ marginBottom: 12 }}>
                  <Select<RequireFilter>
                    value={requireFilter}
                    onChange={(value) => setRequireFilter(value)}
                    style={{ width: 150 }}
                    options={[
                      { value: 'ALL', label: '全部状态' },
                      { value: 'DRAFT', label: '草稿' },
                      { value: 'SUBMITTED', label: '已提交' },
                      { value: 'APPROVED', label: '已审批' },
                      { value: 'ORDERED', label: '已转采购' },
                      { value: 'CLOSED', label: '已关闭' },
                      { value: 'CANCELED', label: '已取消' }
                    ]}
                  />
                  <Input
                    allowClear
                    placeholder="请购期间 yyyy-MM"
                    value={requirePeriod}
                    onChange={(event) => setRequirePeriod(event.target.value)}
                    style={{ width: 180 }}
                  />
                  <Button
                    type="primary"
                    onClick={() => {
                      setRequirePeriodInput(requirePeriod || dayjs().format('YYYY-MM'));
                      setRequireRemark('');
                      setRequireLines([newDraftLine()]);
                      setRequireOpen(true);
                    }}
                  >
                    新建请购单
                  </Button>
                </Space>
                {requires.isError ? (
                  <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="请购单加载失败"
                    description={requires.error instanceof ApiError ? requires.error.friendlyMessage : String(requires.error)}
                  />
                ) : null}
                <Table<RequireRow>
                  rowKey="id"
                  size="middle"
                  columns={requireColumns}
                  dataSource={requireRows}
                  loading={requires.isPending}
                  pagination={false}
                  scroll={{ x: sumWidth(requireColumns) }}
                  locale={{ emptyText: '暂无请购单' }}
                />
                <Space style={{ marginTop: 16 }}>
                  <Typography.Text type="secondary">已加载 {requireRows.length} 条</Typography.Text>
                  {requires.hasNextPage ? (
                    <Button onClick={() => requires.fetchNextPage()} loading={requires.isFetchingNextPage}>
                      加载更多
                    </Button>
                  ) : null}
                </Space>
              </>
            )
          }
        ]}
      />

      <Drawer
        title={detail.data?.order.bizNo ?? '采购订单详情'}
        open={currentId !== null}
        onClose={() => setCurrentId(null)}
        width={960}
      >
        <PurchaseDetailBody
          detail={detail.data}
          loading={detail.isLoading}
          error={detail.error}
          acking={ack.isPending}
          onAck={(id) => ack.mutate(id)}
          onReceive={openReceive}
          statusMeta={ORDER_STATUS}
        />
      </Drawer>

      <Modal
        title="到货收货"
        open={receiveOpen}
        onCancel={() => setReceiveOpen(false)}
        onOk={() => {
          if (!currentId) return;
          if (!warehouseId) {
            message.warning('请填写收货仓库 ID');
            return;
          }
          receive.mutate({ id: currentId, warehouseId, items: receiveDrafts });
        }}
        okText="确认收货"
        confirmLoading={receive.isPending}
        width={720}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Select
            placeholder="收货仓库"
            value={warehouseId || undefined}
            showSearch
            optionFilterProp="label"
            onChange={setWarehouseId}
            options={warehouseOptions}
            style={{ width: '100%' }}
          />
          {receiveDrafts.length === 0 ? (
            <Typography.Text type="secondary">该订单没有待收货明细</Typography.Text>
          ) : (
            receiveDrafts.map((item, index) => (
              <Space key={item.itemId} align="center">
                <Typography.Text style={{ width: 200 }} ellipsis>
                  {item.materialName ?? item.itemId}
                </Typography.Text>
                <InputNumber
                  min={0}
                  max={item.remain}
                  precision={3}
                  value={item.qty}
                  onChange={(value) =>
                    setReceiveDrafts((prev) =>
                      prev.map((row, i) => (i === index ? { ...row, qty: Number(value ?? 0) } : row))
                    )
                  }
                  addonAfter={`/${item.remain}`}
                />
                <Input
                  placeholder="批号（可选）"
                  value={item.batchNo}
                  style={{ width: 180 }}
                  onChange={(event) =>
                    setReceiveDrafts((prev) =>
                      prev.map((row, i) => (i === index ? { ...row, batchNo: event.target.value } : row))
                    )
                  }
                />
              </Space>
            ))
          )}
          <Typography.Text type="secondary">
            收货只生成入库单（WAIT_CHECK），需在
            <Typography.Link onClick={() => navigate('/inventory/inbound')}>「入库单」</Typography.Link>
            页做入库确认后才会增加库存。
          </Typography.Text>
        </Space>
      </Modal>

      <Modal
        title="新建请购单"
        open={requireOpen}
        onCancel={() => setRequireOpen(false)}
        width={860}
        okText="提交请购"
        confirmLoading={createRequire.isPending}
        onOk={() => {
          if (requireLines.every((line) => !line.materialId || line.qty <= 0)) {
            message.warning('请至少录入一行物资与数量');
            return;
          }
          createRequire.mutate();
        }}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Space>
            <Input
              placeholder="请购期间 yyyy-MM"
              value={requirePeriodInput}
              onChange={(event) => setRequirePeriodInput(event.target.value)}
              style={{ width: 180 }}
            />
            <Input
              placeholder="备注（可选）"
              value={requireRemark}
              onChange={(event) => setRequireRemark(event.target.value)}
              style={{ width: 320 }}
            />
          </Space>
          <DraftLineEditor
            lines={requireLines}
            materialOptions={materialOptions}
            onChange={setRequireLines}
            onAppend={() => setRequireLines((prev) => [...prev, newDraftLine()])}
          />
          <Typography.Text type="secondary">
            请购单提交后进入审批流；审批通过后可转为采购单。单价留空按主数据参考价。
          </Typography.Text>
        </Space>
      </Modal>

      <Modal
        title="新建采购单"
        open={purchaseOpen}
        onCancel={() => setPurchaseOpen(false)}
        width={900}
        okText="生成采购单"
        confirmLoading={createPurchase.isPending}
        onOk={() => {
          if (!buySupplierId) {
            message.warning('请选择供应商');
            return;
          }
          if (!buyWarehouseId) {
            message.warning('请选择收货仓库');
            return;
          }
          if (purchaseLines.every((line) => !line.materialId || line.qty <= 0)) {
            message.warning('请至少录入一行物资与数量');
            return;
          }
          createPurchase.mutate();
        }}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Space>
            <Select
              placeholder="供应商"
              style={{ width: 260 }}
              showSearch
              optionFilterProp="label"
              value={buySupplierId || undefined}
              onChange={setBuySupplierId}
              options={supplierOptions}
            />
            <Select
              placeholder="收货仓库"
              style={{ width: 220 }}
              showSearch
              optionFilterProp="label"
              value={buyWarehouseId || undefined}
              onChange={setBuyWarehouseId}
              options={warehouseOptions}
            />
            <Input
              type="date"
              style={{ width: 170 }}
              value={buyExpectArrive}
              onChange={(event) => setBuyExpectArrive(event.target.value)}
            />
          </Space>
          <Input
            placeholder="备注（可选）"
            value={buyRemark}
            onChange={(event) => setBuyRemark(event.target.value)}
          />
          <DraftLineEditor
            lines={purchaseLines}
            materialOptions={materialOptions}
            onChange={setPurchaseLines}
            onAppend={() => setPurchaseLines((prev) => [...prev, newDraftLine()])}
          />
          <Typography.Text type="secondary">
            采购单生成后状态为草稿/已提交，需下达给供应商并由供应商接单；到货在详情页做「收货」，再到「入库单」确认入库。
          </Typography.Text>
        </Space>
      </Modal>
    </Card>
  );
}

/** 建单行编辑器（请购 / 采购共用）。 */
function DraftLineEditor({
  lines,
  materialOptions,
  onChange,
  onAppend
}: {
  lines: DraftLine[];
  materialOptions: { value: string; label: string; material: MaterialRow }[];
  onChange: (next: DraftLine[]) => void;
  onAppend: () => void;
}) {
  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      {lines.map((line, index) => (
        <Space key={line.key} align="center">
          <Select
            placeholder="选择物资"
            style={{ width: 320 }}
            showSearch
            optionFilterProp="label"
            value={line.materialId || undefined}
            options={materialOptions}
            onChange={(value: string) => {
              const picked = materialOptions.find((option) => option.value === value)?.material;
              onChange(
                lines.map((row, i) =>
                  i === index
                    ? {
                        ...row,
                        materialId: value,
                        materialName: picked?.materialName ?? undefined,
                        spec: picked?.spec ?? undefined,
                        unitId: picked?.baseUnitId,
                        unitName: picked?.unitName ?? undefined,
                        unitPrice: row.unitPrice || picked?.referencePrice || 0
                      }
                    : row
                )
              );
            }}
          />
          <InputNumber
            min={0}
            precision={3}
            placeholder="数量"
            value={line.qty}
            onChange={(value) =>
              onChange(lines.map((row, i) => (i === index ? { ...row, qty: Number(value ?? 0) } : row)))
            }
          />
          <InputNumber
            min={0}
            precision={4}
            placeholder="单价"
            value={line.unitPrice}
            onChange={(value) =>
              onChange(lines.map((row, i) => (i === index ? { ...row, unitPrice: Number(value ?? 0) } : row)))
            }
          />
          <Typography.Text type="secondary" style={{ width: 60 }}>
            {line.unitName ?? '-'}
          </Typography.Text>
          <Button
            size="small"
            danger
            disabled={lines.length <= 1}
            onClick={() => onChange(lines.filter((_row, i) => i !== index))}
          >
            删除
          </Button>
        </Space>
      ))}
      <Button size="small" onClick={onAppend}>
        添加一行
      </Button>
    </Space>
  );
}
